import React, { useEffect, useState } from 'react'
import { Link, useParams, useHistory } from 'react-router-dom'
import { PencilIcon, TrashIcon, XIcon } from '@heroicons/react/solid'

import useQuery from '@/hooks/useQuery'
import { useGlobalState } from '@/state'
import { Modal, useModal } from '@/shared/Modal'
import { randomToastSuccess } from '@/shared/Toast'
import Loading from '@/shared/Loading'
import PrimaryButton from '@/shared/Buttons/Primary'

import VoteButton from './VoteButton'
import FeedbackImageUploader from './FeedbackImageUploader'
import { STATUS_LABELS, STATUS_COLORS, CATEGORY_LABELS, CATEGORY_COLORS, formatDate } from './shared'

const Avatar = ({ user, size = 'h-8 w-8' }) => (
  user.imageUrl ? (
    <img src={user.imageUrl} alt='' className={`${size} rounded-full object-cover`} />
  ) : (
    <span className={`${size} inline-flex items-center justify-center rounded-full bg-gray-400 text-white font-medium uppercase text-xs`}>
      {user.displayName?.[0] || '?'}
    </span>
  )
)

const CommentRow = ({ comment, currentUser, onDelete, onImageDeleted }) => {
  const [confirming, setConfirming] = useState(false)
  const owner = currentUser?.id === comment.user.id
  const { putpostRequest } = useQuery()

  const removeImage = (imageId) => {
    putpostRequest(`/api/v1/feedback_images/${imageId}`, 'DELETE', {}, (err) => {
      if (err) return
      onImageDeleted(comment.id, imageId)
    })
  }

  return (
    <div className='py-4 flex gap-3'>
      <Avatar user={comment.user} />
      <div className='flex-1 min-w-0'>
        <div className='flex items-center gap-2 text-xs'>
          <span className='font-semibold text-gray-900 dark:text-gray-100'>{comment.user.displayName}</span>
          <span className='text-gray-500'>{formatDate(comment.createdAt)}</span>
          {owner && (
            <button
              type='button'
              onClick={() => confirming ? onDelete(comment.id) : setConfirming(true)}
              className='ml-auto text-xs text-gray-400 hover:text-red-600 inline-flex items-center gap-1'
            >
              <TrashIcon className='h-3.5 w-3.5' />
              {confirming ? 'Really delete?' : 'Delete'}
            </button>
          )}
        </div>
        <div className='mt-1 text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-words'>{comment.body}</div>
        {comment.images?.length > 0 && (
          <div className='mt-2 flex flex-wrap gap-2'>
            {comment.images.map(img => (
              <div key={img.id} className='relative group'>
                <a href={img.url} target='_blank' rel='noopener noreferrer'>
                  <img src={img.url} alt='' className='h-24 w-24 object-cover rounded-md border border-gray-200 dark:border-gray-700' />
                </a>
                {owner && (
                  <button
                    type='button'
                    onClick={() => removeImage(img.id)}
                    className='absolute top-1 right-1 inline-flex items-center justify-center h-5 w-5 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 hover:bg-black/80'
                    aria-label='Remove image'
                  >
                    <XIcon className='h-3 w-3' />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

const FeedbackShow = () => {
  const { slug } = useParams()
  const history = useHistory()
  const { getRequest, putpostRequest } = useQuery()
  const [, setToast] = useGlobalState('toast')
  const [currentUser] = useGlobalState('currentUser')
  const upvotersModal = useModal()

  const [feedback, setFeedback] = useState(null)
  const [comments, setComments] = useState([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(false)
  const [editForm, setEditForm] = useState(null)
  const [saving, setSaving] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const [commentBody, setCommentBody] = useState('')
  const [commentImages, setCommentImages] = useState([])
  const [postingComment, setPostingComment] = useState(false)

  const [allUpvoters, setAllUpvoters] = useState([])
  const [upvotersMeta, setUpvotersMeta] = useState(null)

  const fetchFeedback = () => {
    getRequest(`/api/v1/feedbacks/${slug}`, {}, (err, json) => {
      setLoading(false)
      if (err || !json?.feedback) return
      setFeedback(json.feedback)
    })
  }

  const fetchComments = () => {
    getRequest(`/api/v1/feedbacks/${slug}/comments`, {}, (err, json) => {
      if (err || !json?.comments) return
      setComments(json.comments)
    })
  }

  useEffect(() => { fetchFeedback(); fetchComments() }, [slug])

  const owner = currentUser?.id && feedback?.user?.id === currentUser.id
  const canEdit = owner && feedback?.status === 'under_review'

  const startEdit = () => {
    setEditForm({
      title: feedback.title,
      description: feedback.description || '',
      category: feedback.category,
      imageUrls: feedback.images.map(i => i.url)
    })
    setEditing(true)
  }

  const saveEdit = () => {
    setSaving(true)
    putpostRequest(`/api/v1/feedbacks/${slug}`, 'PATCH', {
      feedback: { title: editForm.title.trim(), description: editForm.description.trim(), category: editForm.category },
      image_urls: editForm.imageUrls
    }, (err, json) => {
      setSaving(false)
      if (err) return
      setFeedback(json.feedback)
      setEditing(false)
      setToast(randomToastSuccess())
    })
  }

  const deleteFeedback = () => {
    putpostRequest(`/api/v1/feedbacks/${slug}`, 'DELETE', {}, (err) => {
      if (err) return
      history.push('/feedback')
    })
  }

  const removeImage = (imageId) => {
    putpostRequest(`/api/v1/feedback_images/${imageId}`, 'DELETE', {}, (err) => {
      if (err) return
      setFeedback({ ...feedback, images: feedback.images.filter(i => i.id !== imageId) })
    })
  }

  const postComment = () => {
    if (!commentBody.trim()) return
    setPostingComment(true)
    putpostRequest('/api/v1/feedback_comments', 'POST', {
      feedback_id: slug,
      feedback_comment: { body: commentBody.trim() },
      image_urls: commentImages
    }, (err, json) => {
      setPostingComment(false)
      if (err || !json?.comment) return
      setComments([...comments, json.comment])
      setCommentBody('')
      setCommentImages([])
      setFeedback({ ...feedback, commentsCount: feedback.commentsCount + 1 })
    })
  }

  const deleteComment = (commentId) => {
    putpostRequest(`/api/v1/feedback_comments/${commentId}`, 'DELETE', {}, (err) => {
      if (err) return
      setComments(comments.filter(c => c.id !== commentId))
      setFeedback({ ...feedback, commentsCount: Math.max(0, feedback.commentsCount - 1) })
    })
  }

  const handleCommentImageDelete = (commentId, imageId) => {
    setComments(comments.map(c => c.id === commentId
      ? { ...c, images: c.images.filter(i => i.id !== imageId) }
      : c))
  }

  const openUpvoters = () => {
    setAllUpvoters([])
    setUpvotersMeta(null)
    upvotersModal.openModal()
    loadUpvoters(1)
  }

  const loadUpvoters = (page) => {
    getRequest(`/api/v1/feedbacks/${slug}/upvoters`, { page }, (err, json) => {
      if (err) return
      setAllUpvoters(prev => page === 1 ? json.upvoters : [...prev, ...json.upvoters])
      setUpvotersMeta(json.meta)
    })
  }

  if (loading) return <Loading />
  if (!feedback) return <div className='py-8 text-center text-gray-500'>Feedback not found.</div>

  if (feedback.mergedInto) {
    return (
      <div className='py-8 max-w-3xl mx-auto'>
        <Link to='/feedback' className='text-sm text-clcpurple hover:underline'>← Back to feedback</Link>
        <div className='mt-5 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-6'>
          <h2 className='text-lg font-bold text-amber-900 dark:text-amber-100'>This feedback has been merged</h2>
          <p className='mt-2 text-sm text-amber-800 dark:text-amber-200'>
            It was merged into <Link to={`/feedback/${feedback.mergedInto.slug}`} className='font-semibold underline'>{feedback.mergedInto.title}</Link>.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className='py-8 max-w-3xl mx-auto'>
      <Link to='/feedback' className='text-sm text-clcpurple hover:underline'>← Back to feedback</Link>

      <div className='mt-5 bg-white dark:bg-gray-800 rounded-lg shadow p-5'>
        <div className='flex items-start gap-4'>
          <VoteButton slug={slug} voted={feedback.voted} votesCount={feedback.votesCount} size='lg' />

          <div className='flex-1 min-w-0'>
            {editing ? (
              <div className='space-y-3'>
                <div className='flex gap-2'>
                  {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                    <button
                      key={value}
                      type='button'
                      onClick={() => setEditForm({ ...editForm, category: value })}
                      className={`px-3 py-1.5 rounded-md text-xs font-semibold border ${editForm.category === value ? 'bg-clcpurple text-white border-clcpurple' : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600'}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                <input
                  type='text'
                  value={editForm.title}
                  maxLength={200}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                />
                <textarea
                  rows={5}
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                />
                <FeedbackImageUploader urls={editForm.imageUrls} onChange={(urls) => setEditForm({ ...editForm, imageUrls: urls })} max={5} />
                <div className='flex gap-2'>
                  <PrimaryButton onClick={saveEdit} loading={saving} text='Save' />
                  <button type='button' onClick={() => setEditing(false)} className='px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 hover:underline'>Cancel</button>
                </div>
              </div>
            ) : (
              <>
                <div className='flex items-center gap-2 flex-wrap'>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${CATEGORY_COLORS[feedback.category]}`}>{CATEGORY_LABELS[feedback.category]}</span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_COLORS[feedback.status]}`}>{STATUS_LABELS[feedback.status]}</span>
                  {canEdit && (
                    <button type='button' onClick={startEdit} className='ml-auto text-xs text-gray-500 hover:text-clcpurple inline-flex items-center gap-1'>
                      <PencilIcon className='h-3.5 w-3.5' /> Edit
                    </button>
                  )}
                </div>
                <h1 className='mt-2 text-2xl font-bold text-gray-900 dark:text-gray-100'>{feedback.title}</h1>
                <div className='mt-1 flex items-center gap-2 text-xs text-gray-500'>
                  <Avatar user={feedback.user} size='h-5 w-5' />
                  <span>{feedback.user.displayName}</span>
                  <span>·</span>
                  <span>{formatDate(feedback.createdAt)}</span>
                </div>
                {feedback.description && (
                  <div className='mt-4 text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-words'>{feedback.description}</div>
                )}
                {feedback.images?.length > 0 && (
                  <div className='mt-4 flex flex-wrap gap-2'>
                    {feedback.images.map(img => (
                      <div key={img.id} className='relative group'>
                        <a href={img.url} target='_blank' rel='noopener noreferrer'>
                          <img src={img.url} alt='' className='h-32 w-32 object-cover rounded-md border border-gray-200 dark:border-gray-700' />
                        </a>
                        {owner && (
                          <button
                            type='button'
                            onClick={() => removeImage(img.id)}
                            className='absolute top-1 right-1 inline-flex items-center justify-center h-6 w-6 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 hover:bg-black/80'
                            aria-label='Remove image'
                          >
                            <XIcon className='h-3.5 w-3.5' />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {feedback.upvoters?.length > 0 && (
                  <div className='mt-4 flex items-center gap-2'>
                    <span className='text-xs text-gray-500'>Upvoted by</span>
                    <div className='flex -space-x-2'>
                      {feedback.upvoters.slice(0, 5).map(u => (
                        <div key={u.id} className='ring-2 ring-white dark:ring-gray-800 rounded-full'>
                          <Avatar user={u} size='h-6 w-6' />
                        </div>
                      ))}
                    </div>
                    {feedback.votesCount > 5 && (
                      <button type='button' onClick={openUpvoters} className='text-xs text-clcpurple hover:underline'>
                        +{feedback.votesCount - 5} more
                      </button>
                    )}
                  </div>
                )}
                {owner && (
                  <div className='mt-4 pt-3 border-t border-gray-100 dark:border-gray-700'>
                    <button
                      type='button'
                      onClick={() => confirmDelete ? deleteFeedback() : setConfirmDelete(true)}
                      className='text-xs text-gray-400 hover:text-red-600 inline-flex items-center gap-1'
                    >
                      <TrashIcon className='h-3.5 w-3.5' />
                      {confirmDelete ? 'Really delete?' : 'Delete this feedback'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <div className='mt-6'>
        <h2 className='text-lg font-bold text-gray-900 dark:text-gray-100 mb-3'>{feedback.commentsCount} {feedback.commentsCount === 1 ? 'comment' : 'comments'}</h2>

        <div className='bg-white dark:bg-gray-800 rounded-lg shadow divide-y divide-gray-100 dark:divide-gray-700 px-4'>
          {comments.length === 0 ? (
            <div className='py-6 text-center text-sm text-gray-500'>Be the first to comment.</div>
          ) : comments.map(c => (
            <CommentRow key={c.id} comment={c} currentUser={currentUser} onDelete={deleteComment} onImageDeleted={handleCommentImageDelete} />
          ))}
        </div>

        {currentUser?.id ? (
          <div className='mt-4 bg-white dark:bg-gray-800 rounded-lg shadow p-4'>
            <textarea
              rows={3}
              value={commentBody}
              onChange={(e) => setCommentBody(e.target.value)}
              placeholder='Add a comment…'
            />
            <div className='mt-2'>
              <FeedbackImageUploader urls={commentImages} onChange={setCommentImages} max={5} />
            </div>
            <div className='mt-3 flex justify-end'>
              <PrimaryButton onClick={postComment} loading={postingComment} disabled={!commentBody.trim()} text='Post comment' />
            </div>
          </div>
        ) : (
          <p className='mt-4 text-sm text-gray-500 text-center'>
            <a href={`/login?fwd=${encodeURIComponent(`/feedback/${slug}`)}`} className='text-clcpurple font-semibold'>Log in</a> to leave a comment.
          </p>
        )}
      </div>

      <Modal isOpen={upvotersModal.isOpen} closeModal={upvotersModal.closeModal}>
        <h3 className='text-lg font-bold text-gray-900 dark:text-gray-100 mb-3'>Upvoted by</h3>
        <div className='space-y-2 max-h-96 overflow-y-auto'>
          {allUpvoters.map(u => (
            <div key={u.id} className='flex items-center gap-2'>
              <Avatar user={u} />
              <span className='text-sm text-gray-800 dark:text-gray-200'>{u.displayName}</span>
            </div>
          ))}
        </div>
        {upvotersMeta?.nextPage && (
          <div className='mt-3 text-center'>
            <button
              type='button'
              onClick={() => loadUpvoters(upvotersMeta.nextPage)}
              className='text-sm text-clcpurple font-semibold hover:underline'
            >
              Load more
            </button>
          </div>
        )}
      </Modal>
    </div>
  )
}

export default FeedbackShow
